import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Container, Row, Col, Card, Badge, Spinner, Button } from "react-bootstrap";
import '../componentCss/webform.css';
import '../componentCss/sidebar.css';
import { getMasterListAction, getMasterDataDetailsAction, deleteMasterDataAction } from '../../Action/modernAdminActions';
import { decryptData } from './decrypt';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import config from '../../config/config';
import DataTable from 'react-data-table-component';
import ReactToPrint from 'react-to-print';
import { MdEdit, MdAdd, MdFileDownload, MdPrint, MdSearch } from 'react-icons/md';
import { BsFillTrash3Fill, BsTable, BsEye } from "react-icons/bs";
import { FaDatabase, FaList, FaChartBar } from "react-icons/fa";
// XLSX is large; load it on demand at call time to keep bundles small
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import BulkImportButton from './BulkImport/BulkImportButton';
import MasterDataRecordsModal from './ModalComponents/MasterDataRecordsModal';

const Masterdata = () => {
    const [masterList, setMasterData] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [error, setError] = useState(null);
    const allDataRef = useRef();

    // Master Data Records Modal state
    const [selectedTable, setSelectedTable] = useState(null);
    const [showRecordsModal, setShowRecordsModal] = useState(false);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        getMasterList(page, pageSize);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPageSize(newPerPage);
        setCurrentPage(page);
        getMasterList(page, newPerPage);
    };

    useEffect(() => {
        getMasterList(currentPage, pageSize);
    }, []);

    // Optional debug effect (can be removed in production)
    // useEffect(() => {
    // }, [masterList, totalRecords, isLoading]);

    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (searchFilter) {
                setCurrentPage(1);
                getMasterList(1, pageSize, searchFilter);
            } else {
                getMasterList(currentPage, pageSize);
            }
        }, 300);

        return () => clearTimeout(delayedSearch);
    }, [searchFilter]);

    let getMasterList = async (page = 1, limit = 10, search = '') => {
        setIsLoading(true);
        setError(null);
        
        try {
            let res = await getMasterListAction({ page, limit, search });
            
            if (res.success) {
                // Extract the actual data from the service response
                const actualData = res.data.data;
                
                // Optional debug logging (can be removed in production)
                
                if (actualData && actualData.tables && Array.isArray(actualData.tables)) {
                    // New backend response format
                    setMasterData(actualData.tables);
                    setTotalRecords(actualData.total || 0);
                    setHasNext(actualData.has_next || false);
                    setHasPrevious(actualData.has_previous || false);
                    setCurrentPage(actualData.page || 1);
                    setPageSize(actualData.page_size || 10);
                } else if (Array.isArray(actualData)) {
                    // Legacy format - direct array
                    setMasterData(actualData);
                    setTotalRecords(actualData.length);
                } else if (typeof actualData === 'string') {
                    // Legacy encrypted format
                    const decryptedData = decryptData(actualData);
                    const dataArray = Array.isArray(decryptedData) ? decryptedData : [];
                    setMasterData(dataArray);
                    setTotalRecords(dataArray.length);
                } else {
                    setMasterData([]);
                    setTotalRecords(0);
                }
                
                // Don't show success toast every time to avoid spam
                if (searchFilter) {
                    toast.success(`Found ${actualData.total || (actualData.tables ? actualData.tables.length : 0)} tables`);
                }
            } else {
                setError(res.message || "Failed to fetch master list");
                setMasterData([]);
                setTotalRecords(0);
                toast.error(res.message || "Failed to fetch master list");
            }
        } catch (error) {
            setError("An error occurred while fetching master data");
            setMasterData([]);
            setTotalRecords(0);
            toast.error("An error occurred while fetching master data");
        } finally {
            setIsLoading(false);
        }
    }

    const deleteMasterData = async (id, tableName) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `You want to delete the master table "${tableName}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            customClass: {
                confirmButton: 'btn btn-danger me-2',
                cancelButton: 'btn btn-secondary'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    let res = await deleteMasterDataAction(id);
                    if (res.success) {
                        toast.success(res.message || res.msg || 'Master table deleted successfully');
                        // Refresh the current page instead of reloading
                        getMasterList(currentPage, pageSize, searchFilter);
                    } else {
                        toast.error(res.message || res.data || 'Failed to delete master table');
                    }
                } catch (error) {
                    toast.error('An error occurred while deleting the master table');
                }
            }
        });
    };


    const downloadSampleFile = async (dbTable, id) => {
        try {
            toast.loading('Preparing sample file...');
            let getTableFields = await getMasterDataDetailsAction(id);

            if (getTableFields.success) {
                let fields = getTableFields.data.columns;
                const fieldsObj = {};
                const transformed = [];
                fields.forEach((item) => {
                    if (item.name !== 'id') {
                        fieldsObj[item.name] = '';
                    }
                });
                transformed.push(fieldsObj);
                const { utils, write } = await import('xlsx');
                const worksheet = utils.json_to_sheet(transformed);
                const workbook = utils.book_new();
                utils.book_append_sheet(workbook, worksheet, "Sheet1");
                const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
                saveAs(blob, `${dbTable}_sample.xlsx`);
                toast.dismiss();
                toast.success('Sample file downloaded successfully');
            } else {
                toast.dismiss();
                toast.error('Failed to generate sample file');
            }
        } catch (error) {
            toast.dismiss();
            toast.error('An error occurred while downloading the sample file');
        }
    }

    // Handle viewing table records
    const handleViewRecords = (table) => {
        setSelectedTable(table);
        setShowRecordsModal(true);
    };

    const columns = useMemo(() => [
        {
            name: '#',
            cell: (row, index) => {
                return ((currentPage - 1) * pageSize) + index + 1;
            },
            width: '60px',
            center: true,
        },
        {
            name: 'Table Name',
            selector: row => row.name || 'N/A',
            sortable: true,
            cell: row => (
                <div className="d-flex align-items-center">
                    <BsTable className="me-2 text-primary" />
                    <strong>{row.name || 'N/A'}</strong>
                </div>
            ),
            minWidth: '200px',
        },
        {
            name: 'Description',
            selector: row => row.description || 'No description',
            sortable: true,
            cell: row => (
                <span title={row.description}>
                    {row.description && row.description.length > 50 
                        ? `${row.description.substring(0, 50)}...` 
                        : row.description || 'No description'
                    }
                </span>
            ),
            minWidth: '250px',
        },
        {
            name: 'Category',
            selector: row => row.category || 'General',
            sortable: true,
            cell: row => (
                <Badge 
                    bg={row.category === 'Medical' ? 'success' : 
                        row.category === 'Administrative' ? 'primary' : 'secondary'}
                    className="text-white"
                >
                    {row.category || 'General'}
                </Badge>
            ),
            center: true,
            minWidth: '120px',
        },
        {
            name: 'Records',
            selector: row => row.total_records || 0,
            sortable: true,
            cell: row => (
                <div className="d-flex align-items-center">
                    <FaChartBar className="me-2 text-info" />
                    <span>{row.total_records || 0}</span>
                </div>
            ),
            center: true,
            minWidth: '100px',
        },
        {
            name: 'Columns',
            selector: row => row.total_columns || 0,
            sortable: true,
            cell: row => (
                <Badge bg="info" className="text-white">
                    {row.total_columns || 0}
                </Badge>
            ),
            center: true,
            minWidth: '100px',
        },
        {
            name: 'Status',
            selector: row => row.is_active,
            sortable: true,
            cell: row => (
                <Badge 
                    bg={row.is_active ? 'success' : 'danger'}
                    className="text-white"
                >
                    {row.is_active ? 'Active' : 'Inactive'}
                </Badge>
            ),
            center: true,
            minWidth: '100px',
        },
        {
            name: 'Access Level',
            selector: row => row.access_level || 'Private',
            sortable: true,
            cell: row => (
                <Badge 
                    bg={row.access_level === 'public' ? 'warning' : 'secondary'}
                    className={row.access_level === 'public' ? 'text-dark' : 'text-white'}
                >
                    {row.access_level || 'Private'}
                </Badge>
            ),
            center: true,
            minWidth: '120px',
        },
        {
            name: 'Created',
            selector: row => row.created_at,
            sortable: true,
            cell: row => (
                <span title={new Date(row.created_at).toLocaleString('en-GB')}>
                    {new Date(row.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </span>
            ),
            minWidth: '120px',
        },
        {
            name: 'Actions',
            cell: (rowData) => (
                <div className="d-flex gap-2 align-items-center">
                    <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleViewRecords(rowData)}
                        title="View Records"
                    >
                        <BsEye size={16} />
                    </Button>

                    <BulkImportButton
                        tableId={rowData.table_id}
                        tableName={rowData.name}
                        onImportComplete={() => getMasterList(currentPage, pageSize)}
                        buttonText="Import"
                        buttonSize="sm"
                    />

                    <Link
                        to={`${config.baseUrl}edit-master/${rowData.table_id}`}
                        className="btn btn-outline-primary btn-sm"
                        title="Edit Table"
                    >
                        <MdEdit size={16} />
                    </Link>

                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => deleteMasterData(rowData.table_id, rowData.name)}
                        title="Delete Table"
                    >
                        <BsFillTrash3Fill size={14} />
                    </Button>

                    <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => downloadSampleFile(rowData.name, rowData.table_id)}
                        title="Download Sample Excel"
                    >
                        <MdFileDownload size={16} />
                    </Button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            minWidth: '300px',
        }
    ], [currentPage, pageSize, searchFilter]);

    return (
        <>
            <Container fluid className="p-4">
                {/* Header Section */}
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <FaDatabase className="me-3 text-primary" size={24} />
                                        <div>
                                            <h4 className="mb-1 text-primary">Master Data Management</h4>
                                            <p className="mb-0 text-muted">Manage your master tables and reference data</p>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <ReactToPrint
                                            trigger={() => (
                                                <Button variant="outline-secondary" size="sm" className="no-print">
                                                    <MdPrint className="me-1" /> Print
                                                </Button>
                                            )}
                                            content={() => allDataRef.current}
                                        />
                                        <Link to={`${config.baseUrl}add-master`}>
                                            <Button variant="primary" size="sm">
                                                <MdAdd className="me-1" /> Add New Table
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Summary Stats */}
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <BsTable className="text-primary mb-2" size={24} />
                                <h5 className="mb-1">{totalRecords}</h5>
                                <small className="text-muted">Total Tables</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <FaChartBar className="text-success mb-2" size={24} />
                                <h5 className="mb-1">{masterList.filter(item => item.is_active).length}</h5>
                                <small className="text-muted">Active Tables</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <BsEye className="text-info mb-2" size={24} />
                                <h5 className="mb-1">{masterList.filter(item => item.access_level === 'public').length}</h5>
                                <small className="text-muted">Public Tables</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <FaList className="text-warning mb-2" size={24} />
                                <h5 className="mb-1">{masterList.reduce((sum, item) => sum + (item.total_records || 0), 0)}</h5>
                                <small className="text-muted">Total Records</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Search and Filter Section */}
                <Row className="mb-3">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center" style={{ maxWidth: '400px', flex: '1' }}>
                                        <MdSearch className="text-muted me-2" size={20} />
                                        <input
                                            type="text"
                                            className="form-control border-0 bg-light"
                                            placeholder="Search tables by name, description, or category..."
                                            value={searchFilter}
                                            onChange={(e) => setSearchFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className="text-muted">
                                        Showing {masterList.length} of {totalRecords} tables
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Main Data Table */}
                <Row>
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-0">
                                <div ref={allDataRef}>
                                    {error ? (
                                        <div className="text-center py-5">
                                            <div className="text-danger mb-3">
                                                <FaDatabase size={48} />
                                            </div>
                                            <h5 className="text-danger mb-2">Error Loading Data</h5>
                                            <p className="text-muted mb-3">{error}</p>
                                            <Button 
                                                variant="outline-primary" 
                                                onClick={() => getMasterList(currentPage, pageSize, searchFilter)}
                                            >
                                                Try Again
                                            </Button>
                                        </div>
                                    ) : isLoading ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary" className="mb-3" />
                                            <h5 className="text-muted">Loading Master Tables...</h5>
                                            <p className="text-muted mb-0">Please wait while we fetch your data</p>
                                        </div>
                                    ) : masterList.length === 0 ? (
                                        <div className="text-center py-5">
                                            <BsTable className="text-muted mb-3" size={48} />
                                            <h5 className="text-muted mb-2">No Master Tables Found</h5>
                                            <p className="text-muted mb-3">
                                                {searchFilter ? 'No tables match your search criteria.' : 'Get started by creating your first master table.'}
                                            </p>
                                            {!searchFilter && (
                                                <Link to={`${config.baseUrl}add-master`}>
                                                    <Button variant="primary">
                                                        <MdAdd className="me-1" /> Create First Table
                                                    </Button>
                                                </Link>
                                            )}
                                        </div>
                                    ) : (
                                        <DataTable
                                            columns={columns}
                                            data={masterList}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRecords}
                                            paginationDefaultPage={currentPage}
                                            paginationPerPage={pageSize}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            paginationRowsPerPageOptions={[10, 20, 50, 100]}
                                            progressPending={isLoading}
                                            highlightOnHover
                                            pointerOnHover
                                            responsive
                                            striped
                                            customStyles={{
                                                headRow: {
                                                    style: {
                                                        backgroundColor: '#f8f9fa',
                                                        borderBottom: '2px solid #dee2e6',
                                                        fontSize: '14px',
                                                        fontWeight: '600'
                                                    }
                                                },
                                                rows: {
                                                    style: {
                                                        fontSize: '13px',
                                                        '&:hover': {
                                                            backgroundColor: '#f8f9fa',
                                                            cursor: 'pointer'
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Master Data Records Modal */}
            <MasterDataRecordsModal
                show={showRecordsModal}
                onHide={() => setShowRecordsModal(false)}
                table={selectedTable}
            />
        </>
    )
}

export default Masterdata;
